import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpward as SendIcon, Close as CloseIcon } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';

import type { ChatEntry } from '../types';

import { ChatMessageBubble, SystemMessageRow } from './ChatMessageBubble';

const MAX_MESSAGE_LENGTH = 1000;

interface ChatPanelProps {
  messages: ChatEntry[];
  onSend: (text: string) => void;
  onClose: () => void;
  isConnected: boolean;
  localSessionId?: string;
}

export function ChatPanel({ messages, onSend, onClose, isConnected, localSessionId }: ChatPanelProps) {
  const [inputValue, setInputValue] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on new messages (scroll container directly to avoid page scroll)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages.length]);

  // Auto-focus input when panel opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || !isConnected) return;
    onSend(trimmed);
    setInputValue('');
  }, [inputValue, isConnected, onSend]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const canSend = inputValue.trim().length > 0 && isConnected;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 320,
        borderRadius: 2,
        bgcolor: 'grey.900',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          backgroundColor: 'grey.800',
        }}
      >
        <Typography variant="subtitle1" sx={{fontWeight: 600,  color: 'common.white' }}>
          Chat
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close chat">
          <CloseIcon fontSize="small" sx={{ color: 'grey.400' }} />
        </IconButton>
      </Box>

      {/* Message list */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 2,
        }}
      >
        {messages.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'grey.400',
              textAlign: 'center',
              mt: 4,
            }}
          >
            No messages yet
          </Typography>
        ) : (
          messages.map((entry, index) => {
            if (entry.type !== 'chat') {
              return <SystemMessageRow key={entry.id} message={entry} />;
            }
            const prev: ChatEntry | undefined = messages[index - 1];
            let showName = true;
            if (prev && prev.type === 'chat') {
              showName =
                prev.senderId && entry.senderId
                  ? prev.senderId !== entry.senderId
                  : prev.senderRole !== entry.senderRole;
            }
            const isMine =
              entry.senderId && localSessionId
                ? entry.senderId === localSessionId
                : entry.senderRole === 'provider';
            return (
              <ChatMessageBubble
                key={entry.id}
                message={entry}
                isMine={isMine}
                showName={showName}
              />
            );
          })
        )}
      </Box>

      {/* Input area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          p: 1.5,
        }}
      >
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={e => {
            if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
              setInputValue(e.target.value);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={isConnected ? 'Send a message...' : 'Waiting for patient to join...'}
          disabled={!isConnected}
          multiline
          maxRows={5}
          minRows={2}
          size="small"
          fullWidth
         slotProps={{ input: {
            sx: {
              fontSize: '0.875rem',
              color: 'common.white',
              bgcolor: 'grey.900',
              borderRadius: '4px',
              px: 1.5,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isConnected ? 'primary.light' : 'grey.700',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isConnected ? 'primary.light' : 'grey.700',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.light',
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.800',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.700',
                },
              },
              '& input::placeholder, & textarea::placeholder': {
                color: 'grey.400',
                opacity: 1,
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'inherit',
                '&::placeholder': {
                  color: 'grey.500',
                  opacity: 1,
                },
              },
            },
          } }}/>
        <IconButton
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
          size="small"
          sx={{
            bgcolor: canSend ? 'primary.light' : 'grey.700',
            color: 'grey.900',
            '&:hover': { bgcolor: canSend ? 'primary.main' : 'grey.700' },
            '&.Mui-disabled': { color: 'grey.900', bgcolor: 'grey.700' },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
